import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/cms/invoice-scanning/scans';
export const getInvoiceScanByIdEndpointIdentifier = endpoint;

export interface IInvoiceScanItemData {
  id: number;
  invoiceScanId: number;
  description: string;
  quantity: number | null;
  unitPrice: number | null;
  totalPrice: number | null;
  unit: string | null;
  itemCode: string | null;
  matchedOrderItemId: number | null;
  matchConfidence: number | null;
  matchStatus: string;
  discrepancyType: string | null;
  discrepancyDetails: {
    type: string;
    expectedQuantity?: number;
    actualQuantity?: number;
    difference?: number;
    percentageDiff?: number;
  } | null;
  matchedOrderItem: Record<string, any> | null;
}

export interface IDiscrepancy {
  type: string;
  field: string;
  expected: number;
  actual: number;
  difference: number;
  message: string;
}

export interface IInvoiceScanDetailData {
  id: number;
  orderId: number;
  uploadedBy: number;
  originalFileName: string;
  s3Url: string;
  mimeType: string;
  fileSize: number;
  status: string;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  vendorName: string | null;
  subtotal: number | null;
  taxAmount: number | null;
  totalAmount: number | null;
  currency: string | null;
  validationStatus: string;
  processedAt: string | null;
  createdAt: string;
  extractedData: Record<string, any> | null;
  validationErrors: string[] | null;
  discrepancies: IDiscrepancy[] | null;
  processingError: string | null;
  items: IInvoiceScanItemData[];
  order: { id: number; restaurantId: number };
  uploadedByUser: { id: number; name: string };
}

type TData = { data: IInvoiceScanDetailData };
type TError = AxiosError;
type TQueryKey = [string];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetInvoiceScanByIdApi(scanId: string, options?: Options): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();
  const fullEndpoint = `${endpoint}/${scanId}`;

  const queryFn = async () => {
    try {
      const { data } = await http.get(fullEndpoint);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useQueryWrapper([fullEndpoint], queryFn, {
    enabled: isLoggedIn && !!scanId,
    ...options,
  });
}
